import { useState, useMemo, useEffect } from "react";
import { TableColumn } from "../components/common/Table";

/**
 * Inserts keys that exist in `schemaKeys` but not in `saved`, using each key's
 * index in `schemaKeys` so new columns appear in the intended default position
 * (instead of always at the end).
 */
function mergeOrderWithNewSchemaKeys(saved: string[], schemaKeys: string[]): string[] {
  const base = saved.filter((k) => schemaKeys.includes(k));
  for (const sk of schemaKeys) {
    if (base.includes(sk)) continue;
    const pos = schemaKeys.indexOf(sk);
    let insertAt = base.length;
    for (let i = 0; i < base.length; i++) {
      if (schemaKeys.indexOf(base[i]) > pos) {
        insertAt = i;
        break;
      }
    }
    base.splice(insertAt, 0, sk);
  }
  return base;
}

/**
 * Configuration options for the table system
 */
export interface TableConfigOptions {
  /** Unique key for persistent storage (localStorage) */
  persistenceKey?: string;
}

/**
 * Hook to manage table column visibility and reordering state
 */
export const useTableConfig = <T extends object>(
  allColumns: TableColumn<T>[],
  options?: TableConfigOptions
) => {
  const { persistenceKey } = options || {};

  // Initialize ordered keys from storage or defaults
  const [orderedKeys, setOrderedKeys] = useState<string[]>(() => {
    if (persistenceKey) {
      const saved = localStorage.getItem(`${persistenceKey}_order`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) return parsed;
        } catch (e) {
          console.error("Error parsing table order from localStorage", e);
        }
      }
    }
    return allColumns.map((col) => col.key);
  });

  // Initialize visible keys from storage or defaults
  const [visibleKeys, setVisibleKeys] = useState<string[]>(() => {
    if (persistenceKey) {
      const saved = localStorage.getItem(`${persistenceKey}_visibility`);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) return parsed;
        } catch (e) {
          console.error("Error parsing table visibility from localStorage", e);
        }
      }
    }
    return allColumns
      .filter((col) => col.defaultVisible !== false)
      .map((col) => col.key);
  });

  const schemaKeySignature = allColumns.map((c) => c.key).join("|");

  /**
   * When `allColumns` gains keys, merge them into saved order at schema positions.
   * (Does not change visibility — that stays whatever is in localStorage unless you bump `persistenceKey`.)
   */
  useEffect(() => {
    const schemaKeys = allColumns.map((c) => c.key);

    setOrderedKeys((prev) => {
      const valid = prev.filter((k) => schemaKeys.includes(k));
      const missing = schemaKeys.filter((k) => !valid.includes(k));
      if (missing.length === 0) {
        return valid.length === prev.length && valid.every((k, i) => k === prev[i])
          ? prev
          : valid;
      }
      return mergeOrderWithNewSchemaKeys(valid, schemaKeys);
    });
  }, [schemaKeySignature, allColumns]);

  // Persist order changes
  useEffect(() => {
    if (persistenceKey) {
      localStorage.setItem(`${persistenceKey}_order`, JSON.stringify(orderedKeys));
    }
  }, [orderedKeys, persistenceKey]);

  // Persist visibility changes
  useEffect(() => {
    if (persistenceKey) {
      localStorage.setItem(
        `${persistenceKey}_visibility`,
        JSON.stringify(visibleKeys)
      );
    }
  }, [visibleKeys, persistenceKey]);

  /**
   * Toggles the visibility of a column by its key
   */
  const toggleVisibility = (key: string) => {
    const column = allColumns.find((col) => col.key === key);
    // Required columns cannot be hidden
    if (column?.required) return;

    setVisibleKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  /**
   * Reorders columns based on drag-and-drop interaction
   */
  const reorderColumns = (activeId: string, overId: string) => {
    if (activeId === overId) return;

    const activeColumn = allColumns.find((col) => col.key === activeId);
    const overColumn = allColumns.find((col) => col.key === overId);

    if (
      activeColumn?.isReorderable === false ||
      overColumn?.isReorderable === false
    ) {
      return;
    }

    setOrderedKeys((prev) => {
      const oldIndex = prev.indexOf(activeId);
      const newIndex = prev.indexOf(overId);

      if (oldIndex === -1 || newIndex === -1) return prev;

      const newOrder = [...prev];
      newOrder.splice(oldIndex, 1);
      newOrder.splice(newIndex, 0, activeId);
      return newOrder;
    });
  };

  /**
   * Computed columns for the UI based on current order and visibility
   */
  const configuredColumns = useMemo(() => {
    const schemaKeys = allColumns.map((c) => c.key);
    const missingKeys = schemaKeys.filter((k) => !orderedKeys.includes(k));
    const mergedKeys =
      missingKeys.length === 0
        ? orderedKeys.filter((k) => allColumns.some((c) => c.key === k))
        : mergeOrderWithNewSchemaKeys(
            orderedKeys.filter((k) => schemaKeys.includes(k)),
            schemaKeys,
          );
    const fullOrderedCols = mergedKeys
      .map((key) => allColumns.find((col) => col.key === key))
      .filter((col): col is TableColumn<T> => !!col);

    // 3. Filter by visibility (always include required columns)
    return fullOrderedCols.filter(
      (col) => col.required || visibleKeys.includes(col.key)
    );
  }, [allColumns, orderedKeys, visibleKeys]);

  return {
    configuredColumns,
    visibleKeys,
    orderedKeys,
    toggleVisibility,
    reorderColumns,
    allColumns, // Expose for the selection UI
  };
};
